<template>
    <v-card :height="kioskAnalyticsDimension.userSessionheight + 50" flat>
        <v-row>
            <v-col cols="12" md="6">
                <v-card class="pa-4" rounded="lg" elevation="2">
                    <div class="d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline grey--text">TOTAL SESSIONS</div>
                            <div class="text-h4 font-weight-bold">{{ totalSessions }}</div>
                        </div>
                        <v-avatar size="52" color="blue lighten-4">
                            <v-icon large color="blue darken-2">mdi-account-multiple</v-icon>
                        </v-avatar>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline grey--text">TOTAL INTERACTIONS</div>
                            <div class="text-h4 font-weight-bold">{{ totalInteractions }}</div>
                        </div>
                        <v-avatar size="52" color="indigo lighten-4">
                            <v-icon large color="indigo darken-2">mdi-hand-pointing-up</v-icon>
                        </v-avatar>
                    </div>

                    <v-divider class="my-4"></v-divider>

                    <div class="d-flex justify-space-between">
                        <div class="text-body-2 grey--text">Avg interactions / session</div>
                        <div class="text-body-1 font-weight-medium">
                            {{ avgInteractionsPerSession }}
                        </div>
                    </div>
                </v-card>
            </v-col>

            <v-col cols="12" md="6">
                <v-card class="pa-4" rounded="lg" elevation="2">
                    <div class="d-flex align-center justify-space-between">
                        <div>
                            <div class="text-overline grey--text">TOP MODULE</div>
                            <div class="text-h5 font-weight-bold">
                                {{ topModule ? formatModule(topModule.module) : "-" }}
                            </div>
                            <div class="text-subtitle-2 grey--text">
                                {{ topModule ? topModule.total : 0 }} total
                            </div>
                        </div>

                        <v-avatar size="52" color="green lighten-4">
                            <v-icon large color="green darken-2">mdi-star-circle</v-icon>
                        </v-avatar>
                    </div>

                    <v-divider class="my-4"></v-divider>
                    <div class="text-overline grey--text">AI INTERACTION</div>
                    <div class="text-h5 font-weight-bold mb-2">QNA</div>
                    <div class="text-h4 font-weight-bold mb-1">{{ getQnAList.count }}</div>
                    <!-- <v-text-field v-model="search" dense outlined hide-details prepend-inner-icon="mdi-magnify"
                        label="Search modules (e.g. maps, faqs)" />

                    <div class="mt-2 d-flex align-center justify-space-between">
                        <div class="text-body-2 grey--text">Modules shown</div>
                        <div class="text-body-1 font-weight-medium">
                            {{ filteredModules.length }}
                        </div>
                    </div> -->
                </v-card>
            </v-col>
        </v-row>

        <!-- Modules grid -->
        <v-row>
            <v-col cols="12">
                <v-card rounded="lg" elevation="2">
                    <v-card-title class="d-flex align-center">
                        <span class="text-h6 font-weight-bold">Modules Breakdown</span>
                        <v-spacer></v-spacer>
                        <v-chip color="blue lighten-5" text-color="blue darken-3">
                            Total modules: {{ modulesOnly.length }}
                        </v-chip>
                    </v-card-title>

                    <v-divider></v-divider>

                    <v-card-text>
                        <v-row>
                            <v-col v-for="(m, idx) in getModulesCount" :key="idx" cols="12" sm="6" md="4" lg="3">
                                <v-card outlined class="pa-3" rounded="lg">
                                    <div class="d-flex align-center justify-space-between">
                                        <div>
                                            <div class="text-caption grey--text">MODULE</div>
                                            <div class="text-subtitle-1 font-weight-bold">
                                                {{ formatModule(m.module) }}
                                            </div>
                                        </div>

                                        <v-chip :color="chipColor(m.total)" dark class="font-weight-bold">
                                            {{ m.total }}
                                        </v-chip>
                                    </div>

                                    <v-progress-linear class="mt-3" height="8" rounded :value="progressPct(m.total)"
                                        :color="progressColor(m.total)" />
                                    <div class="mt-2 text-caption grey--text">
                                        {{ progressPct(m.total) }}% of top module
                                    </div>
                                </v-card>
                            </v-col>
                        </v-row>

                        <div v-if="filteredModules.length === 0" class="text-center py-8 grey--text">
                            No modules match your search.
                        </div>
                    </v-card-text>
                </v-card>
            </v-col>
        </v-row>
    </v-card>
</template>

<script>
import { mapGetters } from 'vuex'

export default {
    name: 'reports',
    computed: {
        ...mapGetters(['kioskAnalyticsDimension', 'getTotalSessions', 'getTotalInterations', 'getModulesCount', 'getSessionDuration', 'getQnAList']),
        totalSessions() {
            return Number(this.getTotalSessions || 0);
        },
        totalInteractions() {
            return Number(this.getTotalInterations || 0);
        },
        modulesOnly() {
            const raw = this.getModulesCount;

            // getter might be null/undefined at first
            const arr = Array.isArray(raw)
                ? raw
                : raw && typeof raw === "object"
                    ? Object.values(raw)
                    : [];

            return arr
                .filter(x => x && x.module != null)
                .map(x => ({
                    module: String(x.module),
                    total: Number(x.total || 0),
                }))
                .sort((a, b) => b.total - a.total);
        },

        isLoadingModules() {
            // if API hasn't filled it yet
            return !Array.isArray(this.getModulesCount) || this.getModulesCount.length === 0;
        },

        topModule() {
            return this.modulesOnly.length ? this.modulesOnly[0] : null;
        },

        filteredModules() {
            const q = (this.search || "").trim().toLowerCase();
            if (!q) return this.modulesOnly;
            return this.modulesOnly.filter(m =>
                String(m.module).toLowerCase().includes(q)
            );
        },

        avgInteractionsPerSession() {
            if (!this.totalSessions) return "0.00";
            return (this.totalInteractions / this.totalSessions).toFixed(2);
        },

        maxTotal() {
            return this.topModule ? this.topModule.total : 0;
        },
    },
    methods: {
        formatModule(name) {
            const s = String(name || "").replace(/[_-]/g, " ").trim();
            return s ? s.charAt(0).toUpperCase() + s.slice(1) : "-";
        },

        progressPct(val) {
            const max = this.maxTotal || 1;
            const pct = Math.round((Number(val || 0) / max) * 100);
            return Math.max(0, Math.min(100, pct));
        },

        progressColor(val) {
            const pct = this.progressPct(val);
            if (pct >= 70) return "green";
            if (pct >= 35) return "blue";
            return "orange";
        },

        chipColor(val) {
            const pct = this.progressPct(val);
            if (pct >= 70) return "green darken-2";
            if (pct >= 35) return "blue darken-2";
            return "orange darken-2";
        },
    },
    watch: {
        closeWithoutSelection(newVal) {
            if (newVal === 1) {
                //
                this.selectedDuration = 'Today'

            }
        }
    }

}
</script>
<style>
.v-card {
    border-radius: 16px !important;
}
</style>
